import { cloneDeep } from 'lodash';

/**
 * Add default filter to memberQueryUI Object
 * @param {Int} countOfConditions
 * @param {Object} memberQueryUI
 * @returns {Object}
 */
export function addFilter(countOfConditions, memberQueryUI) {
  const filters = memberQueryUI
    ? cloneDeep(memberQueryUI.filters)
    : [];

  const id = !filters.length ? (Math.floor(Math.random() * 1000) + 1)
    : (Math.floor(Math.random() * 100000) + filters.length + 1);

  const defaultFilter = {
    id,
    field: '',
    operator: 'eq',
    parentIndex: countOfConditions <= 1 ? 0 : countOfConditions - 1,
    parentIndexUI: !filters.length ? 1 : filters.length + 1,
    type: 'parent',
    value: '',
  };

  filters.push(defaultFilter);

  return {
    queryType: 'FilterQuery',
    filters,
  };
}

/**
 * Create an id for a filter
 * @param {Int} parentIndexUI
 * @param {Object} filter
 * @returns {Int}
 */
export function getFilterId(parentIndexUI, filter) {
  return filter.type === 'parent'
    ? (Math.floor(Math.random() * 1000) + parentIndexUI)
    : (Math.floor(Math.random() * 100000) + parentIndexUI);
}

/**
 * get array match for pipe un-escaped case
 * @param {String} value
 */
export function regexPipeCharacter(value) {
  const regex = /(?:\\.|[^|\\]+)+/gim;
  return [...value.match(regex)];
}

/**
 * Create array with new include filters. with determined
 * properties to use in component rendering.
 * @param {Object} filter
 * @param {Int} memberQueryUILength
 * @param {Object} newFilter
 * @param {String} operation
 * @returns {Array}
 */
export function buildIncludesFilters(filter, countOfConditions, filterToBuild) {
  const refactorFilters = filter.value.endsWith('|') ? `${filter.value} ` : filter.value;
  const filters = regexPipeCharacter(refactorFilters);
  const count = filters.length > 0 ? (filters.length - 1) : 0;

  const newFilters = filters.map((filterValue, filterIndex) => ({
    ...filterToBuild,
    id: getFilterId(countOfConditions + 1, filterToBuild),
    isLastItem: count === 0 || (filterIndex === count),
    parentIndexUI: countOfConditions + 1,
    type: filterIndex === 0 ? 'parent' : 'child',
    value: filterValue.split('\\|').join('|'),
  }));

  return newFilters;
}

/**
   * Create a copy of memberQueryObject with properties
   * that we can use for UI rendering purposes, in order to
   * support multivalues using the same component
   * (MembershipSuggestionFilterRow.vue).
   * @param {Object} memberQuery
   * @param {Object} memberQueryUI
   * @returns {Object}
   */
export function buildMemberQueryUI(memberQuery) {
  const memberQueryUI = {
    ...memberQuery,
    filters: [],
  };

  let includesFilters;

  if (memberQuery.filters) {
    memberQuery.filters.forEach((filter, index) => {
      const countOfConditions = memberQueryUI.filters.length;
      const newFilter = cloneDeep(filter);
      newFilter.id = (Math.floor(Math.random() * 1000) + (countOfConditions + 1));
      newFilter.parentIndex = index;
      newFilter.parentIndexUI = countOfConditions + 1;
      newFilter.value = newFilter.value.split('\\|').join('|');
      const { operator } = newFilter;

      switch (operator) {
        case 'in':
          includesFilters = buildIncludesFilters(
            filter, countOfConditions, newFilter,
          );
          memberQueryUI.filters = [...memberQueryUI.filters, ...includesFilters];
          break;
        default:
          newFilter.type = 'parent';
          memberQueryUI.filters.push(newFilter);
          break;
      }
    });
  }

  return memberQueryUI;
}

/**
 * Deletes a list item from memberQueryObject
 * @param {Object} filter
 * @param {Object} memberQueryUI
 * @returns {Object}
 */
export function deleteListItem(filter, memberQueryUI) {
  const item = cloneDeep(filter);
  let filters = cloneDeep(memberQueryUI.filters);

  const { id, parentIndexUI, type } = item;

  const relatedFiltersToItem = filters.filter((x) => x.parentIndexUI === parentIndexUI);

  if (filters) {
    if (type === 'parent') {
      filters = filters.filter(x => relatedFiltersToItem.indexOf(x) === -1);
    } else {
      const index = filters.findIndex(x => x.id === id);
      filters.splice(index, 1);

      const parentId = relatedFiltersToItem[0].id;

      const parentIndex = filters.findIndex((f) => f.id === parentId);

      if (relatedFiltersToItem.length === 2) filters[parentIndex].operator = 'eq';
    }
  }

  return {
    queryType: 'FilterQuery',
    filters,
  };
}

/**
   * Delete an item from a string with items split by pipes (|).
   * @param {String} operatorValues
   * @param {Int} indexToRemove
   * @returns {String}
   */
export function deleteItemFromString(operatorValues, indexToRemove) {
  const operatorValuesArray = regexPipeCharacter(operatorValues);
  let result = '';

  if (operatorValuesArray.length < 2) {
    result = `${operatorValuesArray[0]}`;
  } else {
    operatorValuesArray.splice(indexToRemove, 1);
    result = operatorValuesArray.join('|');
  }

  return result;
}

/**
 * replace character with other one in a string
 * @param {String} value
 * @param {Object} memberQueryUI
 * @returns {Boolean}
 */
export function includesPipeCharacter(value) {
  return (value.includes('|'));
}

/**
 * Updates a list item in memberQuery object
 * @param {Object} filter
 * @param {Object} memberQueryUI
 * @returns {Object}
 */
export function updateListItem(filter, memberQueryUI) {
  const item = cloneDeep(filter);
  const filters = cloneDeep(memberQueryUI.filters);

  if (filters) {
    const indexToUpdate = filters.findIndex(x => x.id === item.id);
    filters.splice(indexToUpdate, 1, item);
  }

  return {
    queryType: 'FilterQuery',
    filters,
  };
}
